import { Box, IconButton } from '@material-ui/core';
import { MountElement, SimulatorLoanConditions } from '@nemuru-eng/checkout-js';
import { PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { AutomobileItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileItem';
import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { useDownPayment } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useDownPayment';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { AutoLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/AutoLoan/entities/PaymentMethod/AutoLoanPaymentMethod';
import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useSimulator } from '@/components/shared/hooks/useSimulator';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

type AutoSimulatorProps = {
  paymentMethodEntity: AutoLoanPaymentMethod;
};

// FIXME: This is a hard-coded temporary solution to only filter in EBRO by auto model.
export const EBRO_MERCHANT_IDS = ['b21a57a2-1662-42f1-a8b2-936db6abb090', 'e4cf1153-7568-4d35-b0d1-7757d22e118c'];

export const AutoSimulator = ({ paymentMethodEntity }: AutoSimulatorProps) => {
  const {
    state: { cart, selectedAgent, selectedMerchant, birthDate },
  } = useApplicationContext();

  const { onNext, onPrev } = useApplicationNavigation();
  const { updateCartDiscount } = useCart();

  const { palette, customBorderRadius } = useCustomTheme();
  const { sm } = useScreen();
  const { simulatorComponent } = useSimulator();
  const { setDownPaymentAmount } = useDownPayment();
  const { updatePaymentMethodEntity, paymentMethodEntities, isAnyPaymentMethodSelectedAndConfigured } =
    usePaymentMethods();

  const [SimulatorComponent, setSimulatorComponent] = useState<MountElement | null>(null);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    mountSimulator();
    return () => {
      unmountSimulator();
    };
  }, []);

  const mountSimulator = async () => {
    if (!cart || !selectedAgent || !selectedMerchant) return;

    const auto = cart.items.find((item) => item instanceof AutomobileItem) as AutomobileItem | undefined;

    if (!auto) {
      return;
    }

    // let autoModel: string | undefined;
    // if (EBRO_MERCHANT_IDS.includes(selectedMerchant.id)) {
    //   autoModel = auto.data.versionCode ?? undefined;
    // }

    const Simulator = await simulatorComponent(
      {
        options: {
          disableLocaleSelector: true,
          disableSelectedCard: true,
        },
        boundaries: auto.totalPrice
          ? [
              {
                paymentMethodType: PaymentMethodType.AUTO_LOAN,
                assetAmount: auto.totalPrice,
                registrationDate: auto.registrationDate ?? null,
                ...(birthDate && { dateOfBirth: birthDate }),
                // ...(autoModel && { autoModel }),
                // ...(auto.totalDiscount && { discount: auto.totalDiscount }),
                // downPayment: downPayment.amount.amount,
              },
            ]
          : [],
        onSelected: handleConditionsSelection,
        onError: (error) => console.log('-> onError', error),
      },
      selectedAgent,
    );
    if (!Simulator) return;
    setSimulatorComponent(Simulator);
    Simulator.mount('#auto-nemuru-simulator');
  };

  const unmountSimulator = () => {
    SimulatorComponent?.unmount();
    setSimulatorComponent(null);
  };

  const handleConditionsSelection = (simulatorConditions: SimulatorLoanConditions[] | null): void => {
    updatePaymentMethodEntity(paymentMethodEntity.selectSimulatorConditions(simulatorConditions));

    const downPaymentAmount = simulatorConditions?.[0].downPayment?.value ?? 0;
    setDownPaymentAmount(downPaymentAmount);
  };

  const onSubmit = () => {
    updateCartDiscount(paymentMethodEntities);
    onNext();
  };

  const disabled = !isAnyPaymentMethodSelectedAndConfigured;

  return (
    <>
      <div style={{ position: 'relative' }}>
        <Box position="absolute" top={-40} left={0} right={0} display="flex" justifyContent="flex-end" mb={2}>
          <StandardButton
            size="small"
            color="default"
            variant="outlined"
            onClick={() => setOpened(true)}
            endIcon={<Icon size={16}>open_in_full</Icon>}
          >
            <Text tId={'MAXIMIZE'} />
          </StandardButton>
        </Box>
        {opened && <Box position="fixed" zIndex={100} style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', inset: 0 }} />}
        <CustomPaper
          style={{
            width: 'auto',
            position: opened ? 'fixed' : 'relative',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: palette.background.paper,
            padding: 0,
            ...(opened && { zIndex: 1000, inset: 16, padding: sm ? '16px 24px' : '8px 8px 16px' }),
          }}
        >
          {opened && (
            <IconButton
              size="small"
              style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}
              onClick={() => setOpened(false)}
            >
              <Icon size={16}>close</Icon>
            </IconButton>
          )}
          <div
            style={{
              flexGrow: 1,
              padding: opened ? '16px' : 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {opened && (
              <Box mb={1}>
                <Font variant={'body2'} color={'textSecondary'}>
                  <Text tId={'SELECT_CONDITION_FROM_SIMULATOR'} />
                </Font>
              </Box>
            )}
            <div
              id="auto-nemuru-simulator"
              style={{
                height: '650px',
                overflowY: 'hidden',
                overflowX: 'auto',
                ...(opened && {
                  height: '100%',
                  border: `solid 1px ${palette.divider}`,
                  borderRadius: customBorderRadius.paper,
                }),
              }}
            ></div>
          </div>
          {opened && (
            <div style={{ flexShrink: 0, padding: sm ? '0 24px' : '0 16px' }}>
              <Box width="100%" display="flex" alignItems="center" justifyContent="flex-end" gridGap={1}>
                {onPrev && (
                  <StandardButton startIcon={<Icon size={20}>arrow_back</Icon>} onClick={onPrev}>
                    <Text tId={'BACK'} />
                  </StandardButton>
                )}
                <StandardButton
                  main
                  disabled={disabled}
                  onClick={onSubmit}
                  style={{ minWidth: sm ? '132px' : 0 }}
                  endIcon={<Icon size={20}>arrow_forward</Icon>}
                >
                  <Text tId={'CONTINUE'} />
                </StandardButton>
              </Box>
            </div>
          )}
        </CustomPaper>
      </div>
    </>
  );
};
